import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft, Award, Trophy, Medal, Star, Calendar, CheckCircle,
  Clock, Users, Mail, ExternalLink, AlertCircle, Sparkles,
  Target, TrendingUp, Loader2, Download, Share2
} from 'lucide-react';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seoConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AwardsProgressPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [nomination, setNomination] = useState(null);
  const [isNominationsOpen, setIsNominationsOpen] = useState(false);

  useEffect(() => {
    checkNominationsStatus();
    fetchNominationData();
  }, []);

  const checkNominationsStatus = () => {
    const now = new Date();
    const nominationsOpenDate = new Date('2024-11-01');
    setIsNominationsOpen(now >= nominationsOpenDate);
  };

  const fetchNominationData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/awards-progress`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNomination(data.nomination);
      }
    } catch (error) {
      console.error('Error fetching awards progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-700';
      case 'under_review': return 'bg-yellow-100 text-yellow-700';
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'winner': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const recommendationsRequired = nomination?.category_requirements?.recommendations || 5;
  const recommendationsReceived = nomination?.recommendations?.length || 0;
  const recommendationsProgress = (recommendationsReceived / recommendationsRequired) * 100;

  const milestones = [
    {
      id: 1,
      title: 'Nomination Submitted',
      date: nomination?.submitted_at,
      status: nomination ? 'complete' : 'pending',
      icon: CheckCircle
    },
    {
      id: 2,
      title: 'Recommendations Collected',
      date: recommendationsReceived >= recommendationsRequired ? 'Complete' : 'In Progress',
      status: recommendationsReceived >= recommendationsRequired ? 'complete' : 'pending',
      icon: Users
    },
    {
      id: 3,
      title: 'Under Review',
      date: nomination?.review_started_at || 'Pending',
      status: nomination?.status === 'under_review' || nomination?.status === 'approved' ? 'complete' : 'pending',
      icon: Clock
    },
    {
      id: 4,
      title: 'Winner Announced',
      date: 'January 15, 2025',
      status: nomination?.status === 'winner' ? 'complete' : 'pending',
      icon: Trophy
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-purple-50">
      <SEO 
        title={seoConfig.awards.title}
        description={seoConfig.awards.description}
        canonical={seoConfig.awards.canonical}
        keywords={seoConfig.awards.keywords}
      />
      <div className="container mx-auto px-6 py-8 pt-24">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-xl">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-900">TA Awards Progress</h1>
              <p className="text-slate-600 mt-1">Track your nomination and award status</p>
            </div>
          </div>

          {/* Nominations Status Banner */}
          {!isNominationsOpen && (
            <Card className="bg-gradient-to-r from-yellow-500 to-amber-500 border-0 shadow-xl mb-6">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 text-white">
                  <Calendar className="w-10 h-10" />
                  <div>
                    <h3 className="text-xl font-bold mb-1">Nominations Open November 1st, 2024</h3>
                    <p className="text-yellow-50">Check back soon to submit your nomination for TA Awards 2025!</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-violet-500 animate-spin mb-4" />
            <p className="text-slate-600">Loading your awards progress...</p>
          </div>
        ) : !nomination ? (
          <Card className="bg-white border border-slate-200 shadow-lg">
            <CardContent className="p-12 text-center">
              <Award className="w-20 h-20 text-slate-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">No Active Nomination</h3>
              <p className="text-slate-600 mb-6">
                {isNominationsOpen
                  ? 'Submit your nomination for TA Awards 2025 to start tracking your progress'
                  : 'Nominations will open on November 1st, 2024. Come back to nominate yourself!'}
              </p>
              {isNominationsOpen && (
                <Button
                  onClick={() => navigate('/awards')}
                  className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
                >
                  <Award className="w-5 h-5 mr-2" />
                  Submit Nomination
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Nomination Overview */}
            <Card className="bg-white border border-slate-200 shadow-lg">
              <CardHeader className="border-b border-slate-200 bg-gradient-to-r from-yellow-50 to-amber-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl text-slate-900 mb-2">Your Nomination</CardTitle>
                    <p className="text-slate-600">Category: <strong>{nomination.category}</strong></p>
                  </div>
                  <Badge className={getStatusColor(nomination.status)} style={{ fontSize: '14px', padding: '8px 16px' }}>
                    {nomination.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Submitted</p>
                      <p className="text-sm font-bold text-slate-900">
                        {new Date(nomination.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Recommendations</p>
                      <p className="text-sm font-bold text-slate-900">
                        {recommendationsReceived} of {recommendationsRequired}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Deadline</p>
                      <p className="text-sm font-bold text-slate-900">
                        {nomination.deadline || 'December 31, 2024'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recommendations Progress */}
            <Card className="bg-white border border-slate-200 shadow-lg">
              <CardHeader className="border-b border-slate-200">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-violet-600" />
                  Recommendations Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">
                      {recommendationsReceived} of {recommendationsRequired} recommendations collected
                    </span>
                    <span className="text-sm font-bold text-violet-600">
                      {Math.round(recommendationsProgress)}%
                    </span>
                  </div>
                  <Progress value={recommendationsProgress} className="h-3" />
                </div>

                {recommendationsReceived < recommendationsRequired && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-900 mb-1">
                          Action Required: {recommendationsRequired - recommendationsReceived} more recommendations needed
                        </p>
                        <p className="text-xs text-yellow-700">
                          Share your recommendation link with colleagues, mentors, or industry leaders
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {nomination.recommendations?.map((rec, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{rec.recommender_name}</p>
                          <p className="text-xs text-slate-600">{rec.recommender_title} • {rec.recommender_company}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {new Date(rec.submitted_at).toLocaleDateString()}
                      </Badge>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/awards/recommend/${nomination.nomination_id}`);
                    }}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Copy Recommendation Link
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {/* Send reminder */}}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Reminder
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Milestones Timeline */}
            <Card className="bg-white border border-slate-200 shadow-lg">
              <CardHeader className="border-b border-slate-200">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-violet-600" />
                  Award Journey
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  {milestones.map((milestone, index) => {
                    const Icon = milestone.icon;
                    const isComplete = milestone.status === 'complete';
                    const isLast = index === milestones.length - 1;

                    return (
                      <div key={milestone.id} className="relative">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isComplete
                              ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                              : 'bg-slate-200'
                          }`}>
                            <Icon className={`w-6 h-6 ${isComplete ? 'text-white' : 'text-slate-400'}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`text-lg font-bold ${isComplete ? 'text-slate-900' : 'text-slate-500'}`}>
                              {milestone.title}
                            </h3>
                            <p className="text-sm text-slate-600">{milestone.date}</p>
                          </div>
                          {isComplete && (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          )}
                        </div>
                        {!isLast && (
                          <div className={`absolute left-6 top-12 w-0.5 h-6 ${
                            isComplete ? 'bg-green-500' : 'bg-slate-200'
                          }`}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Updates & Notifications */}
            {nomination.updates && nomination.updates.length > 0 && (
              <Card className="bg-white border border-slate-200 shadow-lg">
                <CardHeader className="border-b border-slate-200">
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-600" />
                    Recent Updates
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    {nomination.updates.map((update, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-violet-50 border border-violet-100 rounded-lg">
                        <Star className="w-5 h-5 text-violet-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{update.message}</p>
                          <p className="text-xs text-slate-600 mt-1">
                            {new Date(update.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AwardsProgressPage;
